import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Search, X, FileText, FolderOpen, Repeat } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useApp } from '@/context/AppContext'
import type { SearchResult } from '@/types'
import { cn } from '@/lib/utils'

const MAX_RESULTS = 10

// Score: title prefix > title substring > tag/description match
function matchScore(query: string, title: string, description?: string | null, tags?: string[]): number {
  const q = query.toLowerCase()
  const t = title.toLowerCase()
  if (t.startsWith(q)) return 3
  if (t.includes(q)) return 2
  if (tags?.some((tag) => tag.toLowerCase().includes(q))) return 1
  if (description?.toLowerCase().includes(q)) return 1
  return 0
}

interface GlobalSearchProps {
  onSelectTask?: (taskId: string) => void
  onSelectProject?: (projectId: string) => void
  onSelectHabit?: (habitId: string) => void
  /** Hide the ⌘K hint to save header width (used when signed out, where the
      wider Sign In button would otherwise wrap the header row) */
  compact?: boolean
}

export function GlobalSearch({ onSelectTask, onSelectProject, onSelectHabit, compact }: GlobalSearchProps) {
  const { tasks, projects } = useApp()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  const projectNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of projects) map.set(p.id, p.name)
    return map
  }, [projects])

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim()
    if (!q) return []

    const scored: Array<{ result: SearchResult; score: number }> = []

    for (const project of projects) {
      const score = matchScore(q, project.name, project.description, project.tags)
      if (score > 0) {
        scored.push({
          score,
          result: {
            type: 'project',
            id: project.id,
            title: project.name,
            description: project.description ?? undefined,
            status: project.status,
            tags: project.tags ?? [],
            priority: project.priority,
            is_habit: false,
            score,
          },
        })
      }
    }

    for (const task of tasks) {
      const score = matchScore(q, task.title, task.description, task.tags)
      if (score > 0) {
        scored.push({
          score,
          result: {
            type: task.is_habit ? 'habit' : 'task',
            id: task.id,
            title: task.title,
            description: task.description ?? undefined,
            status: task.status,
            project_id: task.project_id,
            project_name: projectNames.get(task.project_id),
            tags: task.tags ?? [],
            priority: task.priority,
            deadline: task.deadline ?? undefined,
            is_habit: task.is_habit ?? false,
            score,
          },
        })
      }
    }

    scored.sort((a, b) => b.score - a.score)
    return scored.slice(0, MAX_RESULTS).map((s) => s.result)
  }, [query, tasks, projects, projectNames])

  // Reset highlight when the result list changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [results])

  // Keyboard shortcut to open search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setQuery('')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault()
        handleSelect(results[selectedIndex])
      }
    },
    [results, selectedIndex]
  )

  const handleSelect = (result: SearchResult) => {
    if (result.type === 'project') {
      onSelectProject?.(result.id)
    } else if (result.type === 'habit') {
      onSelectHabit?.(result.id)
    } else {
      onSelectTask?.(result.id)
    }
    setIsOpen(false)
    setQuery('')
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'project':
        return <FolderOpen className="h-4 w-4 text-blue-500" />
      case 'habit':
        return <Repeat className="h-4 w-4 text-orange-500" />
      default:
        return <FileText className="h-4 w-4 text-gray-500" />
    }
  }

  const getPriorityColor = (priority: number) => {
    if (priority >= 5) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    if (priority >= 4) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    if (priority >= 3) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
    return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
  }

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        className="gap-2 text-muted-foreground"
        onClick={() => setIsOpen(true)}
      >
        <Search className="h-4 w-4" />
        {/* The header collapses outward-in as the window narrows (see the
            ladder documented on the header in Dashboard.tsx). Search gives up
            its ⌘K hint first, then its label below 1280px, ending as a plain
            icon button — the affordance phones already get. */}
        <span className="hidden xl:inline">Search...</span>
        {!compact && (
          <kbd className="hidden min-[1400px]:inline-flex ml-2 pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
            <span className="text-xs">⌘</span>K
          </kbd>
        )}
      </Button>
    )
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={() => {
          setIsOpen(false)
          setQuery('')
        }}
      />

      {/* Search Dialog */}
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] sm:w-full max-w-lg z-50">
        <div className="bg-background border rounded-lg shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search tasks, projects, habits..."
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => {
                setIsOpen(false)
                setQuery('')
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Results */}
          <div ref={resultsRef} className="max-h-[300px] overflow-y-auto">
            {query.trim() && results.length === 0 && (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No results found for "{query}"
              </div>
            )}

            {results.map((result, index) => (
              <div
                key={`${result.type}-${result.id}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 cursor-pointer',
                  index === selectedIndex ? 'bg-muted' : 'hover:bg-muted/50'
                )}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {getIcon(result.type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{result.title}</span>
                    {result.type === 'task' && (
                      <Badge variant="outline" className={cn('text-xs', getPriorityColor(result.priority))}>
                        P{result.priority}
                      </Badge>
                    )}
                  </div>
                  {result.project_name && (
                    <div className="text-xs text-muted-foreground truncate">
                      {result.project_name}
                    </div>
                  )}
                </div>
                <Badge variant="secondary" className="text-xs capitalize shrink-0">
                  {result.type}
                </Badge>
              </div>
            ))}
          </div>

          {/* Footer with keyboard hints */}
          {results.length > 0 && (
            <div className="border-t px-3 py-2 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <kbd className="px-1 bg-muted rounded">↑↓</kbd> navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 bg-muted rounded">↵</kbd> select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 bg-muted rounded">esc</kbd> close
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default GlobalSearch
